import React from 'react';
import { BrowserRouter, Routes, Route, NavLink, useLocation, Navigate } from 'react-router-dom';
import './index.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import Dashboard     from './pages/Dashboard';
import EmployeeList  from './pages/EmployeeList';
import EmployeeProfile from './pages/EmployeeProfile';
import AddEmployee   from './pages/AddEmployee';
import Login         from './pages/Login';
import MyProfile     from './pages/MyProfile';
import Submissions   from './pages/Submissions';
import GuestSubmit   from './pages/GuestSubmit';

const ADMIN_NAV = [
  { to:'/',            label:'Dashboard',   d:'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { to:'/employees',   label:'Employees',   d:'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z' },
  { to:'/employees/new', label:'Add Employee', d:'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z' },
  { to:'/submissions', label:'Submissions',  d:'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4', badge:true },
];

function NavIcon({ d }) {
  return <svg fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" style={{width:16,height:16,flexShrink:0}}><path d={d}/></svg>;
}

function Sidebar() {
  const { user, logout } = useAuth();
  const [pending, setPending] = React.useState(0);

  React.useEffect(() => {
    if (user?.role==='admin') {
      import('./utils/api').then(({default:api}) => {
        api.get('/submissions').then(r => setPending(r.data.data.filter(s=>s.status==='pending').length)).catch(()=>{});
      });
    }
  }, [user]);

  return (
    <aside className="sidebar">
      <div className="sidebar-logo"><h1>EHRM</h1><span>Human Resource System</span></div>
      <nav className="sidebar-nav">
        <div className="nav-section">
          <div className="nav-label">{user?.role==='admin'?'Admin Menu':'My Account'}</div>
          {user?.role==='admin' ? ADMIN_NAV.map(n => (
            <NavLink key={n.to} to={n.to} end={n.to==='/'} className={({isActive})=>`nav-item ${isActive?'active':''}`}>
              <NavIcon d={n.d}/>{n.label}
              {n.badge && pending>0 && <span style={{marginLeft:'auto',background:'#ef4444',color:'#fff',borderRadius:'50%',width:18,height:18,fontSize:10,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>{pending}</span>}
            </NavLink>
          )) : (
            <NavLink to="/me" className={({isActive})=>`nav-item ${isActive?'active':''}`}>
              <NavIcon d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              My Profile & Request
            </NavLink>
          )}
        </div>
      </nav>
      <div style={{padding:'16px 10px',borderTop:'1px solid rgba(255,255,255,.08)'}}>
        <button onClick={logout} className="nav-item" style={{width:'100%',border:'none',cursor:'pointer',color:'rgba(255,100,100,.7)'}}>
          <NavIcon d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
          Logout
        </button>
      </div>
    </aside>
  );
}

function PageTitle() {
  const { pathname } = useLocation();
  const map = {'/':'Dashboard','/employees':'Employees','/employees/new':'Add Employee','/submissions':'Submissions','/me':'My Profile & Request'};
  return map[pathname] || (pathname.includes('/employees/')?'Employee Profile':'EHRM');
}

function ProtectedLayout({ children, adminOnly=false }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role!=='admin') return <Navigate to="/me" replace />;
  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <header className="topbar">
          <span className="topbar-title"><PageTitle /></span>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:13,fontWeight:600,color:'var(--navy)'}}>{user.username || `${user.employee?.first_name} ${user.employee?.last_name}`}</div>
              <div style={{fontSize:11,color:'var(--slate-400)',textTransform:'uppercase',letterSpacing:'.5px'}}>{user.role}</div>
            </div>
            <div style={{width:36,height:36,borderRadius:'50%',background:'var(--indigo)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700}}>
              {user.role==='admin'?'HR':(user.employee?.first_name?.[0]||'E').toUpperCase()}
            </div>
          </div>
        </header>
        <main className="page-body">{children}</main>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      {/* Public */}
      <Route path="/login"         element={user ? <Navigate to={user.role==='admin'?'/':'/me'} replace/> : <Login />} />
      <Route path="/request-letter" element={<GuestSubmit />} />

      {/* Admin */}
      <Route path="/"                element={<ProtectedLayout adminOnly><Dashboard /></ProtectedLayout>} />
      <Route path="/employees"       element={<ProtectedLayout adminOnly><EmployeeList /></ProtectedLayout>} />
      <Route path="/employees/new"   element={<ProtectedLayout adminOnly><AddEmployee /></ProtectedLayout>} />
      <Route path="/employees/:id"   element={<ProtectedLayout adminOnly><EmployeeProfile /></ProtectedLayout>} />
      <Route path="/submissions"     element={<ProtectedLayout adminOnly><Submissions /></ProtectedLayout>} />

      {/* Employee */}
      <Route path="/me"              element={<ProtectedLayout><MyProfile /></ProtectedLayout>} />

      <Route path="*"                element={<Navigate to={user?.role==='admin'?'/':user?'/me':'/login'} replace/>} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
